import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderPlus, Pencil, Plus, Timer, Trash2 } from 'lucide-react';
import type {
  Catalog,
  CatalogArea,
  CatalogAreaBody,
  CatalogAreaDetail,
  CatalogItem,
  CatalogItemBody,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/features/desks/desks-queries';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import { catalogsListOptions } from '@/features/catalogs/catalogs-queries';
import { catalogTreeQuery } from '@/features/tickets/tickets-queries';
import { formatSlaGoal } from '@/features/tickets/ticket-sla';

function itemSlaLine(item: CatalogItem): string {
  const parts = [
    item.attendMinutes != null ? `atender ${formatSlaGoal(item.attendMinutes)}` : null,
    item.solveMinutes != null ? `resolver ${formatSlaGoal(item.solveMinutes)}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? `SLA: ${parts.join(' · ')}` : 'SLA herdado da prioridade';
}

export function CatalogsPage() {
  const list = useQuery(catalogsListOptions());
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [catalogDialog, setCatalogDialog] = useState<{ catalog: Catalog | null } | null>(null);

  const catalogs = list.data ?? [];
  const selected =
    catalogs.find((c) => c.id === selectedId) ?? (catalogs.length > 0 ? catalogs[0] : undefined);

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
      {/* ── Lista de catálogos ─────────────────────────────────────────────── */}
      <div className="rounded-lg border bg-card shadow-xs">
        <div className="flex items-center gap-2 p-4">
          <h1 className="text-xl leading-7 font-semibold">Catálogos</h1>
          {list.data ? (
            <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground tabular-nums">
              {list.data.length}
            </span>
          ) : null}
          <div className="ml-auto">
            <Button size="sm" onClick={() => setCatalogDialog({ catalog: null })}>
              <Plus className="size-4" strokeWidth={2.5} />
              Novo
            </Button>
          </div>
        </div>
        {list.isPending ? (
          <div className="space-y-2 border-t p-4" aria-busy>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <EmptyState
            className="border-t"
            message={`Erro ao carregar catálogos: ${errorMessage(list.error)}`}
          />
        ) : catalogs.length === 0 ? (
          <EmptyState className="border-t" message="Nenhum catálogo cadastrado" />
        ) : (
          <ul className="border-t p-2">
            {catalogs.map((c) => {
              const active = selected?.id === c.id;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(c.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                      active ? 'bg-primary/10 font-medium text-primary-pressed' : 'hover:bg-accent'
                    )}
                  >
                    <span
                      className={cn(
                        'size-2 shrink-0 rounded-full',
                        c.active ? 'bg-success' : 'bg-border'
                      )}
                      aria-hidden
                    />
                    <span className="min-w-0 truncate">{c.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── Árvore do catálogo selecionado ─────────────────────────────────── */}
      {selected ? (
        <CatalogTree
          key={selected.id}
          catalog={selected}
          onEdit={() => setCatalogDialog({ catalog: selected })}
        />
      ) : (
        <div className="rounded-lg border bg-card shadow-xs">
          <EmptyState className="h-64" message="Selecione um catálogo para ver áreas e itens" />
        </div>
      )}

      {catalogDialog ? (
        <CatalogDialog
          catalog={catalogDialog.catalog}
          onCreated={(c) => setSelectedId(c.id)}
          onClose={() => setCatalogDialog(null)}
        />
      ) : null}
    </div>
  );
}

function CatalogTree({ catalog, onEdit }: { catalog: Catalog; onEdit: () => void }) {
  const queryClient = useQueryClient();
  const detail = useQuery(catalogTreeQuery(catalog.id));
  const [areaDialog, setAreaDialog] = useState<{ area: CatalogArea | null } | null>(null);
  const [itemDialog, setItemDialog] = useState<{ areaId: number; item: CatalogItem | null } | null>(
    null
  );

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['erp', 'catalogs'] });

  const removeCatalog = useMutation({
    mutationFn: async () => {
      const res = await api<void>(`/catalogs/${catalog.id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  const removeArea = useMutation({
    mutationFn: async (areaId: number) => {
      const res = await api<void>(`/catalogs/areas/${areaId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  const removeItem = useMutation({
    mutationFn: async (itemId: number) => {
      const res = await api<void>(`/catalogs/items/${itemId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  const mutationError = removeCatalog.error ?? removeArea.error ?? removeItem.error;
  const areas = [...(detail.data?.areas ?? [])].sort((a, b) => a.position - b.position);

  return (
    <div className="rounded-lg border bg-card shadow-xs">
      <div className="flex flex-wrap items-center gap-2 p-4">
        <h2 className="text-base font-semibold">{catalog.name}</h2>
        {!catalog.active ? (
          <span className="text-xs text-muted-foreground">Inativo</span>
        ) : null}
        <div className="ml-auto flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="size-3.5" />
            Editar
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAreaDialog({ area: null })}>
            <FolderPlus className="size-3.5" />
            Nova área
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={removeCatalog.isPending}
            onClick={() => {
              if (window.confirm(`Excluir o catálogo "${catalog.name}" e todo o seu conteúdo?`)) {
                removeCatalog.mutate();
              }
            }}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="size-3.5" />
            Excluir
          </Button>
        </div>
      </div>

      {mutationError ? (
        <p className="px-4 pb-2 text-sm text-destructive">{errorMessage(mutationError)}</p>
      ) : null}

      {detail.isPending ? (
        <div className="space-y-2 border-t p-4" aria-busy>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : detail.isError ? (
        <EmptyState
          className="border-t"
          message={`Erro ao carregar catálogo: ${errorMessage(detail.error)}`}
        />
      ) : areas.length === 0 ? (
        <EmptyState className="border-t" message="Nenhuma área cadastrada neste catálogo" />
      ) : (
        <div className="space-y-4 border-t p-4">
          {areas.map((area) => (
            <AreaBlock
              key={area.id}
              area={area}
              onEdit={() => setAreaDialog({ area })}
              onRemove={() => {
                if (window.confirm(`Excluir a área "${area.name}" e seus itens?`)) {
                  removeArea.mutate(area.id);
                }
              }}
              onAddItem={() => setItemDialog({ areaId: area.id, item: null })}
              onEditItem={(item) => setItemDialog({ areaId: area.id, item })}
              onRemoveItem={(item) => {
                if (window.confirm(`Excluir o item "${item.name}"?`)) removeItem.mutate(item.id);
              }}
              removing={removeArea.isPending || removeItem.isPending}
            />
          ))}
        </div>
      )}

      {areaDialog ? (
        <AreaDialog
          catalogId={catalog.id}
          area={areaDialog.area}
          onClose={() => setAreaDialog(null)}
        />
      ) : null}
      {itemDialog ? (
        <ItemDialog
          areaId={itemDialog.areaId}
          item={itemDialog.item}
          onClose={() => setItemDialog(null)}
        />
      ) : null}
    </div>
  );
}

function AreaBlock({
  area,
  onEdit,
  onRemove,
  onAddItem,
  onEditItem,
  onRemoveItem,
  removing,
}: {
  area: CatalogAreaDetail;
  onEdit: () => void;
  onRemove: () => void;
  onAddItem: () => void;
  onEditItem: (item: CatalogItem) => void;
  onRemoveItem: (item: CatalogItem) => void;
  removing: boolean;
}) {
  const items = [...area.items].sort((a, b) => a.position - b.position);
  return (
    <section className="rounded-md border border-divider">
      <div className="flex items-center justify-between gap-2 border-b border-divider bg-muted/60 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="truncate text-sm font-semibold">{area.name}</h3>
          <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-muted-foreground tabular-nums">
            {items.length}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <DeskRowAction label={`Novo item em ${area.name}`} onClick={onAddItem}>
            <Plus className="size-3.5" />
          </DeskRowAction>
          <DeskRowAction label="Renomear área" onClick={onEdit}>
            <Pencil className="size-3.5" />
          </DeskRowAction>
          <DeskRowAction label="Excluir área" destructive disabled={removing} onClick={onRemove}>
            <Trash2 className="size-3.5" />
          </DeskRowAction>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="px-3 py-2.5 text-[13px] text-muted-foreground">Nenhum item nesta área.</p>
      ) : (
        <ul className="divide-y divide-divider">
          {items.map((item) => (
            <li key={item.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className={cn(
                      'size-2 shrink-0 rounded-full',
                      item.active ? 'bg-success' : 'bg-border'
                    )}
                    aria-hidden
                  />
                  <span className={cn('truncate font-medium', !item.active && 'text-muted-foreground')}>
                    {item.name}
                  </span>
                </div>
                <div className="mt-0.5 flex min-w-0 items-center gap-2 pl-4 text-xs text-muted-foreground">
                  <span className="flex shrink-0 items-center gap-1">
                    <Timer className="size-3" aria-hidden />
                    {itemSlaLine(item)}
                  </span>
                  {item.description ? <span className="truncate">· {item.description}</span> : null}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                <DeskRowAction label="Editar item" onClick={() => onEditItem(item)}>
                  <Pencil className="size-3.5" />
                </DeskRowAction>
                <DeskRowAction
                  label="Excluir item"
                  destructive
                  disabled={removing}
                  onClick={() => onRemoveItem(item)}
                >
                  <Trash2 className="size-3.5" />
                </DeskRowAction>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── Dialogs ─────────────────────────────────────────────────────────────────

function CatalogDialog({
  catalog,
  onCreated,
  onClose,
}: {
  catalog: Catalog | null;
  onCreated: (c: Catalog) => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(catalog?.name ?? '');
  const [active, setActive] = useState(catalog?.active ?? true);

  const save = useMutation({
    mutationFn: async () => {
      const res = catalog
        ? await api<Catalog>(`/catalogs/${catalog.id}`, {
            method: 'PATCH',
            body: { name: name.trim(), active },
          })
        : await api<Catalog>('/catalogs', { method: 'POST', body: { name: name.trim() } });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'catalogs'] });
      if (!catalog) onCreated(created);
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    save.mutate();
  };

  return (
    <Dialog open onClose={onClose} title={catalog ? 'Editar catálogo' : 'Novo catálogo'}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="catalog-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="catalog-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        {catalog ? (
          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
            <Switch checked={active} onCheckedChange={setActive} />
            Catálogo ativo
          </label>
        ) : null}
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !name.trim()}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function AreaDialog({
  catalogId,
  area,
  onClose,
}: {
  catalogId: number;
  area: CatalogArea | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(area?.name ?? '');

  const save = useMutation({
    mutationFn: async (body: CatalogAreaBody) => {
      const res = area
        ? await api<CatalogArea>(`/catalogs/areas/${area.id}`, { method: 'PUT', body })
        : await api<CatalogArea>(`/catalogs/${catalogId}/areas`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'catalogs'] });
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    save.mutate({ name: name.trim() });
  };

  return (
    <Dialog open onClose={onClose} title={area ? 'Renomear área' : 'Nova área'}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="area-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="area-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !name.trim()}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** minutos → { h, m } em strings para os inputs (vazio quando null). */
function splitMinutes(minutes: number | null | undefined): { h: string; m: string } {
  if (minutes == null) return { h: '', m: '' };
  return { h: String(Math.floor(minutes / 60)), m: String(minutes % 60) };
}

function joinMinutes(h: string, m: string): number | null {
  const hours = h.trim() === '' ? 0 : Number(h);
  const mins = m.trim() === '' ? 0 : Number(m);
  if (!Number.isInteger(hours) || !Number.isInteger(mins) || hours < 0 || mins < 0) return null;
  const total = hours * 60 + mins;
  return total > 0 ? total : null;
}

function ItemDialog({
  areaId,
  item,
  onClose,
}: {
  areaId: number;
  item: CatalogItem | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(item?.name ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [attend, setAttend] = useState(() => splitMinutes(item?.attendMinutes));
  const [solve, setSolve] = useState(() => splitMinutes(item?.solveMinutes));
  const [active, setActive] = useState(item?.active ?? true);

  const save = useMutation({
    mutationFn: async (body: CatalogItemBody) => {
      const res = item
        ? await api<CatalogItem>(`/catalogs/items/${item.id}`, { method: 'PUT', body })
        : await api<CatalogItem>(`/catalogs/areas/${areaId}/items`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'catalogs'] });
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    save.mutate({
      name: name.trim(),
      description: description.trim() || null,
      attendMinutes: joinMinutes(attend.h, attend.m),
      solveMinutes: joinMinutes(solve.h, solve.m),
      active,
    });
  };

  return (
    <Dialog open onClose={onClose} title={item ? 'Editar item' : 'Novo item'}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="item-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="item-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="item-description">Descrição</Label>
          <Textarea
            id="item-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Opcional"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SlaField
            label="SLA de atendimento"
            idPrefix="item-attend"
            value={attend}
            onChange={setAttend}
          />
          <SlaField
            label="SLA de solução"
            idPrefix="item-solve"
            value={solve}
            onChange={setSolve}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Sem SLA preenchido, o item herda os prazos da prioridade do ticket.
        </p>
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          <Switch checked={active} onCheckedChange={setActive} />
          Item ativo
        </label>
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !name.trim()}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function SlaField({
  label,
  idPrefix,
  value,
  onChange,
}: {
  label: string;
  idPrefix: string;
  value: { h: string; m: string };
  onChange: (v: { h: string; m: string }) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-h`}>{label}</Label>
      <div className="flex items-center gap-1.5">
        <Input
          id={`${idPrefix}-h`}
          type="number"
          min={0}
          value={value.h}
          onChange={(e) => onChange({ ...value, h: e.target.value })}
          placeholder="0"
          aria-label={`${label}: horas`}
          className="w-16"
        />
        <span className="text-sm text-muted-foreground">h</span>
        <Input
          id={`${idPrefix}-m`}
          type="number"
          min={0}
          max={59}
          value={value.m}
          onChange={(e) => onChange({ ...value, m: e.target.value })}
          placeholder="0"
          aria-label={`${label}: minutos`}
          className="w-16"
        />
        <span className="text-sm text-muted-foreground">min</span>
      </div>
    </div>
  );
}
